import { useEffect, useRef, useState } from 'react';
import Cropper from 'react-easy-crop';
import { ImagePlus, Loader2, Trash2 } from 'lucide-react';
import { authService } from '../../../services/api';
import { useNotification } from '../../../components/Notifications/NotificationContext';
import { cropToBlob } from '../../../utils/cropImage';

/**
 * Foto do topo dos checkouts da Spectrum (cartão, SPEI, Nequi...). O seller
 * escolhe uma imagem, recorta num círculo e ela é salva na hora (independe do
 * "Salvar" da página). Sem foto própria, o checkout usa a foto do Telegram.
 */
export default function CheckoutAvatar({ botId, hasAvatar }) {
    const { showError, showSuccess } = useNotification();
    const inputRef = useRef(null);
    const [savedUrl, setSavedUrl] = useState(null);
    const [source, setSource] = useState(null);   // imagem escolhida, sendo recortada
    const [crop, setCrop] = useState({ x: 0, y: 0 });
    const [zoom, setZoom] = useState(1);
    const [area, setArea] = useState(null);
    const [busy, setBusy] = useState(false);

    const showBlob = (blob) => setSavedUrl((prev) => {
        if (prev) URL.revokeObjectURL(prev);
        return blob ? URL.createObjectURL(blob) : null;
    });

    useEffect(() => {
        if (!botId || !hasAvatar) return;
        authService.bots.get_checkout_avatar(botId)
            .then(({ data }) => showBlob(data))
            .catch(() => showBlob(null));
    }, [botId, hasAvatar]);

    useEffect(() => () => { if (savedUrl) URL.revokeObjectURL(savedUrl); }, [savedUrl]);

    const choose = (e) => {
        const file = e.target.files?.[0];
        e.target.value = '';
        if (!file) return;
        if (!file.type.startsWith('image/')) {
            showError('Escolha uma imagem (JPG, PNG ou WEBP)');
            return;
        }
        const reader = new FileReader();
        reader.onload = () => {
            setCrop({ x: 0, y: 0 });
            setZoom(1);
            setSource(reader.result);
        };
        reader.readAsDataURL(file);
    };

    const save = async () => {
        if (!area) return;
        setBusy(true);
        try {
            const blob = await cropToBlob(source, area);
            await authService.bots.upload_checkout_avatar(botId, blob);
            showBlob(blob);
            setSource(null);
            showSuccess('Foto do checkout salva');
        } catch (err) {
            showError(err?.response?.data?.error || err?.message || 'Erro ao salvar a foto');
        } finally {
            setBusy(false);
        }
    };

    const remove = async () => {
        setBusy(true);
        try {
            await authService.bots.delete_checkout_avatar(botId);
            showBlob(null);
            showSuccess('Foto removida: o checkout volta a usar a do Telegram');
        } catch (err) {
            showError(err?.response?.data?.error || 'Erro ao remover a foto');
        } finally {
            setBusy(false);
        }
    };

    return (
        <div>
            <span className="pset-field-label">Foto</span>
            <div className="flex items-center gap-4">
                <div className="w-16 h-16 rounded-full overflow-hidden bg-white/[0.06] border border-white/10 flex items-center justify-center shrink-0">
                    {savedUrl
                        ? <img src={savedUrl} alt="Foto do checkout" className="w-full h-full object-cover rounded-full" />
                        : <ImagePlus size={20} className="text-white/40" aria-hidden="true" />}
                </div>
                <label className="btn-secondary cursor-pointer inline-flex items-center gap-2">
                    <ImagePlus size={14} />
                    {savedUrl ? 'Trocar foto' : 'Escolher foto'}
                    <input ref={inputRef} type="file" accept="image/png,image/jpeg,image/webp"
                        aria-label="Escolher foto" className="sr-only" onChange={choose} disabled={busy} />
                </label>
                {savedUrl && (
                    <button type="button" className="btn-secondary inline-flex items-center gap-2" onClick={remove} disabled={busy}>
                        <Trash2 size={14} className="text-red-400" /> Remover
                    </button>
                )}
            </div>
            {!savedUrl && <p className="pset-option-hint mt-2">Sem foto aqui, usa a foto do Telegram.</p>}

            {source && (
                <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 p-4" role="dialog" aria-label="Recortar foto do checkout">
                    <div className="w-full max-w-sm rounded-xl border border-white/10 bg-[#10141a] p-4 text-white">
                        <p className="text-sm font-semibold mb-3">Ajuste a foto</p>
                        <div className="relative w-full aspect-square rounded-lg overflow-hidden bg-black">
                            <Cropper image={source} crop={crop} zoom={zoom} aspect={1} cropShape="round" showGrid={false}
                                onCropChange={setCrop} onZoomChange={setZoom}
                                onCropComplete={(_, pixels) => setArea(pixels)} />
                        </div>
                        <label className="block text-[11px] text-white/60 mt-3">
                            Zoom
                            <input type="range" min={1} max={3} step={0.05} value={zoom}
                                onChange={(e) => setZoom(Number(e.target.value))} className="w-full mt-1" />
                        </label>
                        <div className="flex justify-end gap-2 mt-4">
                            <button type="button" className="btn-secondary" onClick={() => setSource(null)} disabled={busy}>
                                Cancelar
                            </button>
                            <button type="button" className="btn-primary inline-flex items-center gap-2" onClick={save} disabled={busy || !area}>
                                {busy && <Loader2 size={14} className="animate-spin" />} Salvar foto
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
}
